import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Modal, Badge, Spinner, Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import styles from '../Patient.module.css';
import useAxios from '../../../services/AxiosConfig';
import { formatDateString } from '../../../utils/dateUtils';
import { fetchDeviceTrackingData } from '../../../utils/deviceTrackingUtils';
import { showAlert } from '../../../components/PerinHealthAlertSlice';

const DeviceManagementView = () => {
  const dispatch = useDispatch();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [processingAction, setProcessingAction] = useState(null);
  const [actionLogs, setActionLogs] = useState([]);
  const patientInfo = useSelector((state) => state.patient);
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const httpService = useAxios(axiosConfig.envURL);
  
  const [patchInfo, setPatchInfo] = useState({serialNumber: "Unknown", macAddress: "Unknown"});
  const [apiDeviceData, setApiDeviceData] = useState(null);
  
  const [deviceNotes, setDeviceNotes] = useState([]);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [selfTestResults, setSelfTestResults] = useState(null);
  const [loadingSelfTest, setLoadingSelfTest] = useState(false);
  const [patchMetadata, setPatchMetadata] = useState(null);
  const [loadingActions, setLoadingActions] = useState(false);
  const [latestRecordingTimestamp, setLatestRecordingTimestamp] = useState(null);
  const [isDeviceOnTrack, setIsDeviceOnTrack] = useState(true);
  const [modalityTrackingStatus, setModalityTrackingStatus] = useState({});
  const [rebootCount, setRebootCount] = useState(null);
  const [lastRebootTimestamp, setLastRebootTimestamp] = useState(null);

  // Fetch device notes (use same API as DeviceInformationContent)
  const fetchDeviceNotes = React.useCallback(async () => {
    const deviceId = apiDeviceData?.id || patchInfo.serialNumberId;
    if (!deviceId || deviceId === 0) {
      return;
    }

    try {
      const response = await httpService.get(`/device/notes/${deviceId}`);
      if ((response.data?.success || response.data?.code === 200) && Array.isArray(response.data?.data)) {
        const formattedNotes = response.data.data.map((noteObj) => ({
          note: noteObj.notes,
          timestamp: noteObj.createdAt,
        }));
        setDeviceNotes(formattedNotes);
      } else {
        setDeviceNotes([]);
      }
    } catch (err) {
      console.error('Error fetching device notes:', err);
      setDeviceNotes([]);
    }
  }, [apiDeviceData?.id, patchInfo.serialNumberId, httpService]);

  // Add new note (use same API as DeviceInformationContent)
  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setIsSubmittingNote(true);
    try {
      const deviceId = apiDeviceData?.id || patchInfo.serialNumberId;
      await httpService.put(`/device/notes/${deviceId}`, {
        notes: newNote.trim(),
      });

      await fetchDeviceNotes(); // Refresh notes
      setNewNote('');
      setShowAddNoteModal(false);
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  // Fetch self-test results
  const fetchSelfTestResults = React.useCallback(async () => {
    // Use patient/account ID instead of device ID
    const accountId = patientInfo.id;
    if (!accountId) {
      return;
    }

    setLoadingSelfTest(true);
    try {
      // Fetch latest self-test result using accountId
      const response = await httpService.get(`/notifications/selfTestResults/${accountId}`);
      if ((response.data?.code === 200 || response.data?.success) && Array.isArray(response.data?.data) && response.data.data.length > 0) {
        setSelfTestResults(response.data.data[0]);
      } else {
        setSelfTestResults(null);
      }
    } catch (err) {
      console.error('Error fetching self-test results:', err);
      setSelfTestResults(null);
    } finally {
      setLoadingSelfTest(false);
    }
  }, [patientInfo.id, httpService]);

  // Fetch health patch metadata (hardware/software versions and reboot tracking)
  const fetchPatchMetadata = React.useCallback(async () => {
    const accountId = patientInfo.id;
    if (!accountId) {
      return;
    }

    try {
      // Fetch full metadata array to analyze reboot history
      const metadataArrayResponse = await httpService.get(`/measurements/patch/metadata/${accountId}`);
      
      if ((metadataArrayResponse.data?.success || metadataArrayResponse.data?.code === 200) && 
          metadataArrayResponse.data?.data && 
          Array.isArray(metadataArrayResponse.data.data) && 
          metadataArrayResponse.data.data.length > 0) {
          
          const metadataArray = metadataArrayResponse.data.data;
          
          // Sort by createdDate descending to get most recent first
          const sortedMetadata = [...metadataArray].sort((a, b) => 
              new Date(b.createdDate) - new Date(a.createdDate)
          );
          
          // Use the latest metadata for device schedule and display
          const latestMetadata = sortedMetadata[0];
          setPatchMetadata(latestMetadata);
          
          // Process reboot count
          const currentRebootCount = latestMetadata.rebootCount || null;
          setRebootCount(currentRebootCount);
          
          // Find when the reboot count last increased
          let rebootTimestamp = null;
          if (sortedMetadata.length > 1 && currentRebootCount !== null) {
              // Look through sorted metadata to find where reboot count changed
              for (let i = 0; i < sortedMetadata.length - 1; i++) {
                  const currentCount = sortedMetadata[i].rebootCount || 0;
                  const previousCount = sortedMetadata[i + 1].rebootCount || 0;
                  
                  if (currentCount > previousCount) {
                      // Found where reboot count increased
                      rebootTimestamp = sortedMetadata[i].createdDate;
                      break;
                  }
              }
              
              // If no increase found, use the latest metadata timestamp
              if (!rebootTimestamp) {
                  rebootTimestamp = latestMetadata.createdDate;
              }
          } else {
              // Single metadata entry or no reboot count, use latest timestamp
              rebootTimestamp = latestMetadata.createdDate || null;
          }
          
          setLastRebootTimestamp(rebootTimestamp);
      } else {
          // If array is empty, try fetching latest metadata as fallback
          const latestMetadataResponse = await httpService.get(`/measurements/patch/metadata/latest/${accountId}`);
          if ((latestMetadataResponse.data?.success || latestMetadataResponse.data?.code === 200) && latestMetadataResponse.data?.data) {
              const latestData = latestMetadataResponse.data.data;
              setPatchMetadata(latestData);
              setRebootCount(latestData.rebootCount || null);
              setLastRebootTimestamp(latestData.createdDate || null);
          } else {
              setPatchMetadata(null);
              setRebootCount(null);
              setLastRebootTimestamp(null);
          }
      }
    } catch (err) {
      console.error('Error fetching patch metadata:', err);
      setPatchMetadata(null);
      setRebootCount(null);
      setLastRebootTimestamp(null);
    }
  }, [patientInfo.id, httpService]);

  // Fetch health patch actions (action history)
  const fetchActionHistory = React.useCallback(async () => {
    const accountId = patientInfo.id;
    if (!accountId) {
      return;
    }

    setLoadingActions(true);
    try {
      const response = await httpService.get(`/device/health-patch-actions/${accountId}`);
      // API returns {success: true, data: [...]} where data is an array of actions
      if ((response.data?.success || response.data?.code === 200) && Array.isArray(response.data?.data)) {
        setActionLogs(response.data.data);
      } else {
        setActionLogs([]);
      }
    } catch (err) {
      console.error('Error fetching action history:', err);
      setActionLogs([]);
    } finally {
      setLoadingActions(false);
    }
  }, [patientInfo.id, httpService]);

  // Effect to fetch data when dependencies change
  // (moved below after functions are defined)

  useEffect(() => {
    const getPatchInfo = () => {
      const currentPrograms = patientInfo.carePrograms.filter(program => program.enrollment.dischargeDate === null);
      
      if (currentPrograms.length > 0) {
        const perinPatch = currentPrograms[0].devices.find(device => device.deviceId === 1);
        if (perinPatch) {
          return {serialNumber: perinPatch.serialNumber, macAddress: perinPatch.macAddress, serialNumberId: perinPatch.serialNumberId}
        }
      }
      
      let perinPatch = null;
      for (const program of patientInfo.carePrograms) {
        if (Array.isArray(program.devices)) {
          perinPatch = program.devices.find(device => device.deviceId === 1);
          if (perinPatch) break;
        }
      }
      
      // If we found a patch in the programs, use it
      if (perinPatch) {
        return {serialNumber: perinPatch.serialNumber, macAddress: perinPatch.macAddress, serialNumberId: perinPatch.serialNumberId};
      }

      return {serialNumber: "Unknown", macAddress: "Unknown", serialNumberId: 0}
    };

    const info = getPatchInfo();
    setPatchInfo(info);
  }, [patientInfo.carePrograms]);


  // Fetch device data from API
  // Function to fetch device data
  const fetchDeviceData = React.useCallback(async () => {
    if (!patchInfo.serialNumberId || patchInfo.serialNumberId === 0) {
      return;
    }

    try {
      const response = await httpService.get(`/device/all/${patientInfo.id}`);
      
      if (response.data?.data && Array.isArray(response.data.data)) {
          // Find the specific device by serialNumberId
          const device = response.data.data.find(d => d.id === patchInfo.serialNumberId);
          
          if (device) {
            setApiDeviceData(device);
          } else {
            // If device not found, try to find by serialNumber as fallback
            const deviceBySerial = response.data.data.find(d => d.serialNumber === patchInfo.serialNumber);
            if (deviceBySerial) {
              setApiDeviceData(deviceBySerial);
            } else {
              // If still not found, use the first device or create a default structure
              const firstDevice = response.data.data[0];
              if (firstDevice) {
                setApiDeviceData(firstDevice);
              } else {
                // Create a default device structure with null values
                setApiDeviceData({
                  id: null,
                  serialNumber: null,
                  macAddress: null,
                  isActive: false,  
                  isAssigned: false,
                  patientId: null,
                  dateAssigned: null,
                  lastConnection: null,
                  lastDataTransmissionTime: null,
                  currentSoftwareVersion: null,
                  hardwareVersion: null,
                  initialSoftwareVersion: null,
                  housingVersion: null,
                  electronicsManufacturerName: null,
                  electronicsManufacturerDate: null,
                  electronicsManufacturerBatch: null,
                  plasticsManufacturerName: null,
                  plasticsManufacturerDate: null,
                  plasticsManufacturerBatch: null,
                  assemblyManufacturerName: null,
                  assemblyManufacturerDate: null,
                  assemblyManufacturerBatch: null,
                  batteryLotNumber: null,
                  initialTestingDate: null,
                  checkInOutStatus: null,
                  teamMemberAssigned: null,
                  checkOutReason: null,
                  checkOutDateTime: null,
                  checkInDateTime: null,
                  studyId: null,
                  studySite: null,
                  collectionInfo: null,
                  dataRepo: null,
                  isRefurbished: null,
                  checkOutSoftware: null,
                  organizationName: null,
                  groupName: null,
                  firstName: null,
                  lastName: null
                });
              }
            }
          }
        } 
      } catch (err) {
        console.error('Error fetching device data:', err);
      }
    }, [patchInfo.serialNumberId, patchInfo.serialNumber, patientInfo.id, httpService]);

    // Fetch device-specific data when device IDs change
    useEffect(() => {
      // Only fetch if we have a patient ID and serial number ID
      if (patientInfo.id && patchInfo.serialNumberId) {
        Promise.all([
          fetchDeviceData(),
          fetchDeviceNotes()
        ]).catch(err => console.error('Error fetching device-specific data:', err));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientInfo.id, patchInfo.serialNumberId]);

    // Fetch patient-level data only when patient ID changes
    useEffect(() => {
      if (patientInfo.id) {
        Promise.all([
          fetchSelfTestResults(),
          fetchActionHistory(),
          fetchPatchMetadata()
        ]).catch(err => console.error('Error fetching patient-level data:', err));
        // Note: fetchLatestRecordingTimestamp is called in another useEffect after patchMetadata is loaded
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientInfo.id]);

  // Recalculate on-track status when patchMetadata changes
  useEffect(() => {
    const recalculateOnTrackStatus = async () => {
      if (!patientInfo.id) return;

      const { latestTimestamp, isOnTrack, modalityStatus } = await fetchDeviceTrackingData(
        patientInfo.id,
        httpService,
        patchMetadata
      );
      
      if (latestTimestamp) {
        setLatestRecordingTimestamp(latestTimestamp);
      }
      setIsDeviceOnTrack(isOnTrack);
      setModalityTrackingStatus(modalityStatus || {});
    };

    recalculateOnTrackStatus();
  }, [patchMetadata, patientInfo.id, httpService]);

  // Format date for display in user's local time using shared utility + user preference
  const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
  const userId = useSelector((state) => state.userInfo.id);

  // Format date + time for display in user's local time
  const formatDateTime = (input) => {
    if (!input) return 'N/A';
    try {
      const d = new Date(input);
      const datePart = formatDateString(d.toISOString(), userDateFormat);
      const timePart = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return `${datePart}, ${timePart}`;
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Format time only for display
  const formatTimeOnly = (input) => {
    if (!input) return 'N/A';
    try {
      const d = new Date(input);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Render modality tracking status indicator
  const renderModalityStatus = (scheduleKey) => {
    const status = modalityTrackingStatus[scheduleKey];
    
    if (!status || !status.hasData) {
      return (
        <span style={{
          display: 'inline-block',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: '#6c757d',
          marginRight: '8px',
          verticalAlign: 'middle'
        }} title="Insufficient data" />
      );
    }

    const color = status.isOnTrack ? '#28a745' : '#dc3545';
    const title = status.isOnTrack 
      ? `On Track (${status.timeDelta.toFixed(1)} mins between readings)`
      : `Off Track (${status.timeDelta.toFixed(1)} mins between readings, expected ${status.frequency} mins)`;

    return (
      <span style={{
        display: 'inline-block',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: color,
        marginRight: '8px',
        verticalAlign: 'middle',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
      }} title={title} />
    );
  };

  // Temporary mitigation for backend UTC+7 note timestamps: subtract 7 hours
  const adjustUtcBug = (input) => {
    try {
      const d = new Date(input);
      return new Date(d.getTime() - 7 * 60 * 60 * 1000).toISOString();
    } catch (e) {
      return input;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'normal':
      case 'active':
        return '#28a745';
      case 'warning':
        return '#ffc107';
      case 'error':
      case 'critical':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  // Get overall test status from test results
  const getOverallTestStatus = () => {
    if (!selfTestResults || !selfTestResults.testResults) {
      return { status: 'Unknown', color: '#6c757d' };
    }
    
    const allPassed = selfTestResults.testResults.every(test => test.test_status === 1);
    return {
      status: allPassed ? 'Passed' : 'Failed',
      color: allPassed ? '#28a745' : '#dc3545'
    };
  };

  // Get test status color
  const getTestStatusColor = (testStatus) => {
    return testStatus === 1 ? '#28a745' : '#dc3545';
  };

  // Format test name for display
  const formatTestName = (testName, testCode) => {
    const nameMap = {
      'Mem': 'Memory Check',
      'Sen': 'Sensor Calibration',
      'Com': 'Communication Test',
      'Bat': 'Battery Test',
      'Sig': 'Signal Test',
      'AUDIO': 'Audio Check'
    };
    // Use test_name if available, otherwise use test_code as fallback
    const displayName = testName || testCode || 'Unknown Test';
    return nameMap[displayName] || displayName;
  };

  const getActionDescription = (type) => {
    switch (type) {
      case 'self-test':
        return 'Initiate a remote self-test on this device?';
      case 'battery':
        return 'Request battery delivery for this device?';
      case 'reset':
        return 'Reset this device? This will clear all temporary data.';
      case 'firmware':
        return 'Push firmware update to this device?';
      default:
        return 'Proceed with this action?';
    }
  };

  const handleAction = (type) => {
    setActionType(type);
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    setShowConfirmModal(false);
    setProcessingAction(actionType);

    const accountId = patientInfo.id;
    if (!accountId) {
      setProcessingAction(null);
      return;
    }

    try {
      // Handle self-test separately with the new endpoint
      if (actionType === 'self-test') {
        const response = await httpService.post(`/notifications/selfTest/${accountId}`);
        if ((response.data?.success || response.data?.code === 200) && response.status >= 200 && response.status < 300) {
          // Also save the action to HealthPatchActions table
          await httpService.post(`/device/health-patch-actions/${accountId}`, { 
            deviceControl: 'Initiate Self-Test',
            issuerId: userId
          });

          dispatch(showAlert({
            header: 'Self-Test Initiated',
            message: response.data?.message || 'Self-test has been successfully triggered for this device.',
            type: 'success',
            dismissable: false
          }));

          // Refresh action history and self-test results
          await fetchActionHistory();
          setTimeout(() => {
            fetchSelfTestResults();
          }, 30000); // Wait a bit for the test to complete
        } else {
          dispatch(showAlert({
            header: 'Self-Test Failed',
            message: response.data?.message || 'Failed to trigger self-test. Please try again.',
            type: 'danger',
            dismissable: false
          }));
        }
      } else if (actionType === 'firmware') {
        // Handle firmware update with the new endpoint
        const response = await httpService.post(`/notifications/softwareUpdate/${accountId}`);
        if ((response.data?.success || response.data?.code === 200) && response.status >= 200 && response.status < 300) {
          // Also save the action to HealthPatchActions table
          await httpService.post(`/device/health-patch-actions/${accountId}`, { 
            deviceControl: 'Push Firmware Update',
            issuerId: userId
          });

          dispatch(showAlert({
            header: 'Software Update Initiated',
            message: response.data?.message || 'Software update has been successfully triggered for this device.',
            type: 'success',
            dismissable: false
          }));

          // Refresh action history
          await fetchActionHistory();
        } else {
          dispatch(showAlert({
            header: 'Software Update Failed',
            message: response.data?.message || 'Failed to trigger software update. Please try again.',
            type: 'danger',
            dismissable: false
          }));
        }
      } else {
        // Handle other actions (battery, reset) with the existing endpoint
        let deviceControl = '';

        switch (actionType) {
          case 'battery':
            deviceControl = 'Request Batteries';
            break;
          case 'reset':
            deviceControl = 'Reset Device';
            break;
          default:
            break;
        }

        await httpService.post(`/device/health-patch-actions/${accountId}`, { 
          deviceControl,
          issuerId: userId
        });
        
        dispatch(showAlert({
          header: 'Action Completed',
          message: `${deviceControl} has been successfully initiated.`,
          type: 'success',
          dismissable: false
        }));
        
        // Refresh action history to get the latest data from the server
        await fetchActionHistory();
      }
    } catch (err) {
      console.error('Error creating action:', err);
      
      dispatch(showAlert({
        header: 'Action Failed',
        message: 'An error occurred while processing your request. Please try again.',
        type: 'danger',
        dismissable: false
      }));
    } finally {
      setProcessingAction(null);
    }
  };

  const ActionButton = ({ label, type, variant = "primary", icon = null, disabled = false }) => (
    <Button
      variant={variant}
      className="d-flex align-items-center justify-content-center w-100 mb-3"
      style={{ 
        height: '48px',
        backgroundColor: variant === "primary" ? '#1D5769' : undefined,
        border: 'none',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}
      onClick={() => handleAction(type)}
      disabled={disabled || processingAction === type}
    >
      {processingAction === type ? (
        <Spinner
          as="span"
          animation="border"
          size="sm"
          role="status"
          aria-hidden="true"
          className="me-2"
        />
      ) : icon && (
        <span className="me-2">{icon}</span>
      )}
      {label}
    </Button>
  );

  return (
    <div className={styles.ContentContainer}>
      <div className={styles.ContentBody}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="mb-2">
              Health Patch
              <span className="ms-3">
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: getStatusColor(isDeviceOnTrack ? 'Active' : 'Inactive'),
                  marginRight: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} />
                <span style={{ fontSize: '0.7em', fontWeight: 'bold' }}>
                  {isDeviceOnTrack ? 'On Track' : 'Off Track'}
                </span>
              </span>
            </h3>
            <div>
              <span className="me-3"><strong>Serial Number:</strong> {(patchMetadata?.serialNumber && patchMetadata.serialNumber !== 'string') ? patchMetadata.serialNumber : (apiDeviceData?.serialNumber && apiDeviceData.serialNumber !== 'string' ? apiDeviceData.serialNumber : 'N/A')}</span>
              <span className="me-3"><strong>Mac Address:</strong> {apiDeviceData?.macAddress ?? "Unknown"}</span>
              <span className="me-3"><strong>Reboot Count:</strong> {rebootCount !== null ? rebootCount : 'N/A'}</span>
              {lastRebootTimestamp && (
                <span><strong>Latest Reboot:</strong> {formatDateTime(lastRebootTimestamp)}</span>
              )}
            </div>
            <div className="mt-2">
              <span className="me-3">
                <strong>Last Connection: </strong>{formatDateTime(patchMetadata?.createdDate || latestRecordingTimestamp || (apiDeviceData?.lastConnection ? adjustUtcBug(apiDeviceData.lastConnection) : null))}
              </span>
              <span className="me-3">
                <strong>Last Data Transmission: </strong>{formatDateTime((apiDeviceData?.lastDataTransmissionTime ? adjustUtcBug(apiDeviceData.lastDataTransmissionTime) : null) || latestRecordingTimestamp)}
              </span>
              <span>
                <strong>Software Version: </strong>{(patchMetadata?.appSoftwareVersion && patchMetadata.appSoftwareVersion !== 'string') ? patchMetadata.appSoftwareVersion : (apiDeviceData?.currentSoftwareVersion && apiDeviceData.currentSoftwareVersion !== 'string' ? apiDeviceData.currentSoftwareVersion : 'N/A')}
              </span>
            </div>
          </div>
        </div>
        
        <Row>
          <Col lg={3}>
            {/* Device Controls */}
            <Card className="shadow-sm mb-4">
              <Card.Body>
                <Card.Title>Device Controls</Card.Title>
                <div className="mt-3">
                  <ActionButton 
                    label="Initiate Self-Test"
                    type="self-test"
                    variant="primary"
                  />
                  <ActionButton 
                    label="Request Batteries"
                    type="battery"
                    variant="warning"
                  />
                  <ActionButton 
                    label="Push Firmware Update"
                    type="firmware"
                    variant="danger"
                  />
                </div>
              </Card.Body>
            </Card>

            {/* Action History */}
            <Card className="shadow-sm mb-4">
              <Card.Body>
                <Card.Title>Action History</Card.Title>
                <div className="mt-3">
                  {loadingActions ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </div>
                  ) : actionLogs.length > 0 ? (
                    actionLogs.map((log, index) => (
                      <div key={index} className="mb-3 pb-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong>{log.deviceControl}</strong>
                          </div>
                          <small className="text-muted">
                            {formatDateTime(log.createdDate)}
                          </small>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-muted text-center py-3">
                      No actions available
                    </div>
                  )}
                </div>
              </Card.Body>
             </Card>
           </Col>

           <Col lg={3}>
             {/* Manufacturing Information */}
             <Card className="shadow-sm mb-4">
              <Card.Body>
                <Card.Title>Manufacturing Information</Card.Title>
                <div className="mt-3">
                  <Row className="mb-2">
                    <Col xs={6}><strong>Hardware Version:</strong> {patchMetadata?.deviceSoftwareVersion || apiDeviceData?.hardwareVersion || 'N/A'}</Col>
                    <Col xs={6}><strong>Software Version:</strong> {patchMetadata?.appSoftwareVersion || apiDeviceData?.currentSoftwareVersion || 'N/A'}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={12}><strong>Housing Version:</strong> {apiDeviceData?.housingVersion || 'N/A'}</Col>
                  </Row>
                  <div className="mt-3">
                    <h6>Electronics</h6>
                    <div className="ms-3 mb-2">
                      <div><strong>Manufacturer:</strong> {apiDeviceData?.electronicsManufacturerName || 'N/A'}</div>
                      <div><strong>Date:</strong> {apiDeviceData?.electronicsManufacturerDate ? formatDateString(apiDeviceData.electronicsManufacturerDate, userDateFormat) : 'N/A'}</div>
                      <div><strong>Batch #:</strong> {apiDeviceData?.electronicsManufacturerBatch || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <h6>Plastics</h6>
                    <div className="ms-3 mb-2">
                      <div><strong>Manufacturer:</strong> {apiDeviceData?.plasticsManufacturerName || 'N/A'}</div>
                      <div><strong>Date:</strong> {apiDeviceData?.plasticsManufacturerDate ? formatDateString(apiDeviceData.plasticsManufacturerDate, userDateFormat) : 'N/A'}</div>
                      <div><strong>Batch #:</strong> {apiDeviceData?.plasticsManufacturerBatch || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <h6>Assembly Contract Manufacturer</h6>
                    <div className="ms-3 mb-2">
                      <div><strong>Manufacturer:</strong> {apiDeviceData?.assemblyManufacturerName || 'N/A'}</div>
                      <div><strong>Date:</strong> {apiDeviceData?.assemblyManufacturerDate ? formatDateString(apiDeviceData.assemblyManufacturerDate, userDateFormat) : 'N/A'}</div>
                      <div><strong>Batch #:</strong> {apiDeviceData?.assemblyManufacturerBatch || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <strong>Initial Testing Date:</strong> {apiDeviceData?.initialTestingDate ? formatDateString(apiDeviceData.initialTestingDate, userDateFormat) : 'N/A'}
                  </div>
                 </div>
               </Card.Body>
             </Card>
           </Col>

           <Col lg={3}>
             {/* Device Information */}
             <Card className="shadow-sm mb-4">
               <Card.Body>
                 <Card.Title>Device Information</Card.Title>
                 <div className="mt-3">
                   <div className="mb-2">
                     <strong>Device Status: </strong>
                     <span style={{
                       display: 'inline-block',
                       width: '10px',
                       height: '10px',
                       borderRadius: '50%',
                       backgroundColor: isDeviceOnTrack ? '#28a745' : '#dc3545',
                       marginRight: '10px',
                       marginLeft: '4px',
                       verticalAlign: 'middle'
                     }} />
                     {isDeviceOnTrack ? 'Active' : 'Inactive'}
                   </div>
                   <div className="mb-2">
                     <strong>Date Assigned: </strong>{apiDeviceData?.dateAssigned ? formatDateString(apiDeviceData.dateAssigned, userDateFormat) : 'N/A'}
                   </div>
                  <div className="mb-2">
                    <strong>Free Memory Size: </strong>{patchMetadata?.freeMemorySize ? `${(patchMetadata.freeMemorySize / (1024 * 1024)).toFixed(2)} MB` : 'N/A'}
                  </div>
                   {patchMetadata?.deviceSchedule && (
                     <div>
                       <strong>Device Schedule:</strong>
                       <div className="ms-3 mt-2">
                         <div>
                           {renderModalityStatus('ecgMins')}
                           <strong>ECG:</strong> {patchMetadata.deviceSchedule.ecgMins} mins
                         </div>
                         <div>
                           {renderModalityStatus('ppgMins')}
                           <strong>PPG:</strong> {patchMetadata.deviceSchedule.ppgMins} mins
                         </div>
                         <div>
                           {renderModalityStatus('audioMins')}
                           <strong>Audio:</strong> {patchMetadata.deviceSchedule.audioMins} mins
                         </div>
                         <div>
                           {renderModalityStatus('tempMins')}
                           <strong>Temperature:</strong> {patchMetadata.deviceSchedule.tempMins} mins
                         </div>
                         <div>
                           {renderModalityStatus('biozMins')}
                           <strong>Bioimpedance:</strong> {patchMetadata.deviceSchedule.biozMins} mins
                         </div>
                       </div>
                     </div>
                   )}
                 </div>
               </Card.Body>
             </Card>

             {/* Device Notes */}
             <Card className="shadow-sm mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Card.Title className="mb-0">Device Notes</Card.Title>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowAddNoteModal(true)}
                    style={{ backgroundColor: '#1D5769', border: 'none' }}
                  >
                    Add Note
                  </Button>
                </div>
                <div className="mt-3">
                  {deviceNotes.map((note, index) => (
                    <div key={index} className="mb-3 pb-3 border-bottom">
                      <div className="text-muted mb-1">{formatDateTime(adjustUtcBug(note.timestamp))}</div>
                      <div>{note.note}</div>
                    </div>
                  ))}
                  {deviceNotes.length === 0 && (
                    <div className="text-muted text-center py-3">
                      No notes available
                    </div>
                  )}
                 </div>
               </Card.Body>
             </Card>
           </Col>

           <Col lg={3}>
             {/* Clinical Information */}
             <Card className="shadow-sm mb-4">
               <Card.Body>
                 <Card.Title>Clinical Information</Card.Title>
                 <div className="mt-3">
                   <div className="mb-2">
                     <strong>Study ID: </strong>{apiDeviceData?.studyId || 'N/A'}
                   </div>
                   <div className="mb-2">
                     <strong>Study Site: </strong>{apiDeviceData?.studySite || 'N/A'}
                   </div>
                   <div className="mb-2">
                     <strong>Collection Info: </strong>{apiDeviceData?.collectionInfo || 'N/A'}
                   </div>
                   <div className="mb-2">
                     <strong>Organization: </strong>{apiDeviceData?.organizationName || 'N/A'}
                   </div>
                   <div>
                     <strong>Team Member Assigned: </strong>{apiDeviceData?.teamMemberAssigned || 'N/A'}
                   </div>
                 </div>
               </Card.Body>
             </Card>

             {/* Latest Self-Test Results */}
             <Card className="shadow-sm mb-4">
               <Card.Body style={{ minHeight: '250px' }}>
                 <Card.Title>Latest Self-Test Results</Card.Title>
                 {loadingSelfTest ? (
                   <div className="text-center py-4">
                     <Spinner animation="border" role="status">
                       <span className="visually-hidden">Loading...</span>
                     </Spinner>
                   </div>
                 ) : selfTestResults ? (
                   <>
                     <p className="text-muted mb-3">
                       {formatDateTime(selfTestResults.completedDate || selfTestResults.testDate)}
                     </p>
                     <div className="mb-3 fw-bold">
                       Overall Result: <span style={{
                         color: getOverallTestStatus().color
                       }}>{getOverallTestStatus().status}</span>
                     </div>
                     <div>
                       {selfTestResults.testResults && selfTestResults.testResults.length > 0 ? (
                         selfTestResults.testResults.map((test, index) => (
                           <div key={index} className="ms-3 mb-2 d-flex align-items-center">
                             <span style={{
                               width: '8px',
                               height: '8px',
                               borderRadius: '50%',
                               backgroundColor: getTestStatusColor(test.test_status),
                               marginRight: '8px'
                             }} />
                             {formatTestName(test.test_name, test.test_code)} {test.test_number}: <span className="ms-2" style={{
                               color: getTestStatusColor(test.test_status),
                               fontWeight: 'bold'
                             }}>{test.test_status === 1 ? 'Pass' : 'Fail'}</span>
                           </div>
                         ))
                       ) : (
                         <div className="text-muted">No test details available</div>
                       )}
                     </div>
                   </>
                 ) : (
                   <div className="text-muted text-center py-4">
                     No self-test results available
                   </div>
                 )}
               </Card.Body>
             </Card>
           </Col>
        </Row>

        <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Action</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {getActionDescription(actionType)}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={confirmAction}
              style={{ backgroundColor: '#1D5769', border: 'none' }}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add Note Modal */}
        <Modal show={showAddNoteModal} onHide={() => setShowAddNoteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Device Note</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Note Content</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Enter your note here..."
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddNoteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddNote}
              disabled={isSubmittingNote || !newNote.trim()}
              style={{ backgroundColor: '#1D5769', border: 'none' }}
            >
              {isSubmittingNote ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                'Save Note'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default DeviceManagementView; 
